import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  IconButton,
  Tooltip,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import PayrollViewController from "../../redux/actions/payrollApiAction";
import ExportButton from "../../components/ag grid/ExportButton";
import PayslipsMobileView from "./PayslipsMobileView";
import "../../styles/styles.css";
import "../../styles/layout.css";

const MONTH_OPTIONS = [
  { value: 1, name: "January" },
  { value: 2, name: "February" },
  { value: 3, name: "March" },
  { value: 4, name: "April" },
  { value: 5, name: "May" },
  { value: 6, name: "June" },
  { value: 7, name: "July" },
  { value: 8, name: "August" },
  { value: 9, name: "September" },
  { value: 10, name: "October" },
  { value: 11, name: "November" },
  { value: 12, name: "December" },
];

const PaySlips = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const employeeMode = location.pathname.includes("/employee_Payslips");

  const { getPayslip, getEmployeePaySlip } = PayrollViewController();
  const { payslips } = useSelector((state) => state.payslip);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(getEmployeePaySlip());
  }, [dispatch]);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let year = currentYear; year >= 2020; year -= 1) {
      years.push(year);
    }
    return years;
  }, [currentYear]);

  const filteredPayslips = useMemo(
    () =>
      (payslips || []).filter((p) => {
        const payslipDate = new Date(p.month);
        const month = payslipDate.getMonth() + 1;
        const year = payslipDate.getFullYear();
        return month === selectedMonth && year === selectedYear;
      }),
    [payslips, selectedMonth, selectedYear]
  );

  const periodLabel = useMemo(() => {
    const monthName =
      MONTH_OPTIONS.find((m) => m.value === selectedMonth)?.name ||
      selectedMonth;
    return `${monthName} ${selectedYear}`;
  }, [selectedMonth, selectedYear]);

  const handleDownload = useCallback(
    (record) => {
      const fullDate = new Date(record.month);
      const month = fullDate.getMonth() + 1;
      const year = fullDate.getFullYear();
      dispatch(
        getPayslip(record.employee, month, year, record.employee_id)
      );
    },
    [dispatch, getPayslip]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        maxWidth: 50,
        pinned: "left",
      },
      {
        headerName: "SR.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        maxWidth: 80,
      },
      {
        headerName: "Employee Id",
        field: "employee_id",
        flex: 0.7,
      },
      {
        headerName: "Employee",
        field: "employee_name",
        flex: 1,
      },
      {
        headerName: "Month",
        field: "month",
        flex: 0.7,
        valueFormatter: (params) => {
          const date = new Date(params.value);
          return date.toLocaleString("default", {
            month: "long",
            year: "numeric",
          });
        },
      },
      {
        headerName: "Net Salary",
        valueGetter: (params) =>
          params.data.salary?.net_salary?.toFixed(2) ?? "N/A",
        flex: 1,
      },
      {
        headerName: "Generated At",
        field: "generated_at",
        flex: 1,
        valueFormatter: (params) =>
          params.value ? new Date(params.value).toLocaleDateString() : "",
      },
      {
        headerName: "Actions",
        field: "actions",
        pinned: "right",
        maxWidth: 100,
        cellRenderer: (params) => (
          <Tooltip title="Download Payslip">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleDownload(params.data)}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [handleDownload]
  );

  if (isMobile) {
    return (
      <PayslipsMobileView
        payslips={filteredPayslips}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        monthOptions={MONTH_OPTIONS}
        yearOptions={yearOptions}
        onDownload={handleDownload}
        periodLabel={periodLabel}
        employeeMode={employeeMode}
      />
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between mb-2">
        {selectedRows.length > 0 ? (
          <ExportButton
            gridRef={gridRef}
            selectedRows={selectedRows}
            fileName={`Payslips_${selectedMonth}_${selectedYear}.csv`}
            exportColumns={[
              "employee_name",
              "month",
              "year",
              "net_salary",
              "generated_on",
            ]}
          />
        ) : (
          <div />
        )}
      </div>

      <div className="label-card">
        <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
          <h3 className="mb-0">Payslips List</h3>
          <div className="d-flex gap-3">
            <FormControl size="small" style={{ minWidth: 100 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 3 * 48, overflowY: "auto" },
                  },
                }}
              >
                {MONTH_OPTIONS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" style={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 3 * 48, overflowY: "auto" },
                  },
                }}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      <div className="list-grid-container" style={{ height: "70vh" }}>
        <ReusableGrid
          rowData={filteredPayslips}
          columnDefs={columnDefs}
          gridRef={gridRef}
          pagination={true}
          onSelectionChanged={(rows) => setSelectedRows(rows)}
          emptyMessage="No payslips found"
          emptyHint={`No records for ${periodLabel}. Try another month or year.`}
        />
      </div>
    </div>
  );
};

export default PaySlips;
